/* =========================================================================
   Noah's Ark parade — ambient background motion for the Bible Stories round.

   Eric 2026-08-22: one ambient world per category, not all of them — the
   fish belong to The Chosen, and this is the second one: pairs of animals
   walking two-by-two along the bottom of the screen while a Bible Stories
   question is up, with a dove crossing high above now and then. The round's
   own subtitle is "Noah, Jonah & more", so the parade IS the content.

   Gate is the ROUND, not the served question: Bible Stories' questions carry
   story-name categories ("Noah", "Jonah", "Exodus", "Job"…), so there is no
   single category string to key on. A round is Bible Stories content when it
   is the Bible Stories round itself or the player picked Scripture for it.

   Same inert/idempotent/reduced-motion contract as the fish (ui/ambientLayer).
   ========================================================================= */
import { syncAmbientLayer } from './ambientLayer';

export const ARK_LAYER_CLASS = 'ark';

/** One walking pair in the procession. */
export interface ArkWalker {
  /** The animal, as an emoji — rendered twice ("two by two"). */
  emoji: string;
  /** Seconds for one full crossing; bigger animals take longer. */
  dur: number;
  /** Negative seconds: how far into its crossing the pair starts. */
  delay: number;
  scale: number;
  /** Bob period in seconds — gives each species its own gait. */
  gait: number;
}

/**
 * The procession, in marching order. Everyone walks left→right (toward the
 * ark, off-screen right) — one direction reads as a procession; mixed
 * directions would read as a zoo. Delays are spread so the line is already
 * on screen when the question appears and never bunches into a clump.
 */
export const ARK_WALKERS: readonly ArkWalker[] = [
  { emoji: '🦒', dur: 40, delay: -4,  scale: 1.25, gait: 1.9 },
  { emoji: '🐘', dur: 44, delay: -15, scale: 1.3,  gait: 2.3 },
  { emoji: '🦁', dur: 36, delay: -24, scale: 1.05, gait: 1.5 },
  { emoji: '🐑', dur: 34, delay: -31, scale: 0.9,  gait: 1.2 },
  { emoji: '🐪', dur: 42, delay: -9,  scale: 1.15, gait: 2.0 },
  { emoji: '🦓', dur: 38, delay: -20, scale: 1.0,  gait: 1.4 },
  { emoji: '🐢', dur: 60, delay: -50, scale: 0.75, gait: 2.8 },
];

/** The dove's crossing high above — long, slow, one at a time. */
export const ARK_DOVE = { dur: 52, delay: -18, top: 14 } as const;

/**
 * True when a round is Bible Stories content: the Bible Stories round by name,
 * or any round the player pointed at Scripture in the picker. PURE.
 */
export function isBibleStoriesRound(roundName: string | null | undefined, groupKey: string | null | undefined): boolean {
  const n = typeof roundName === 'string' ? roundName.trim().toLowerCase() : '';
  if (n === 'bible stories') return true;
  const g = typeof groupKey === 'string' ? groupKey.trim().toLowerCase().replace(/[-_]+/g, ' ') : '';
  return g === 'scripture' || g === 'bible' || g === 'bible stories';
}

/** Markup for the parade. PURE — callers decide whether to show it. */
export function arkLayerHtml(): string {
  const walkers = ARK_WALKERS.map(
    (w) =>
      `<div class="ark-w" style="animation-duration:${w.dur}s;animation-delay:${w.delay}s;--ark-s:${w.scale};--ark-gait:${w.gait}s">` +
      `<span class="ark-a" aria-hidden="true">${w.emoji}</span>` +
      `<span class="ark-a ark-a2" aria-hidden="true">${w.emoji}</span>` +
      `</div>`,
  ).join('');
  const dove =
    `<div class="ark-dove" style="top:${ARK_DOVE.top}%;animation-duration:${ARK_DOVE.dur}s;animation-delay:${ARK_DOVE.delay}s">` +
    `<span aria-hidden="true">🕊️</span></div>`;
  return `<div class="ark-ground"></div>${dove}${walkers}`;
}

/** Mount/unmount the parade in `host` (idempotent; reduced motion never mounts). */
export function syncArkParade(host: HTMLElement | null, on: boolean): boolean {
  return syncAmbientLayer(host, ARK_LAYER_CLASS, arkLayerHtml, on);
}
